import { useCallback, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../api/config';
import { useApiDocs, hmacSha256 } from '../../../components/apidocs';
import { PLAYGROUND_ENDPOINTS } from '../data/playground';

export type PlaygroundResponse = { status: number; data: any; time: number };

/**
 * สถานะ + ตรรกะทั้งหมดของแท็บ API Playground
 * (ถูกเรียกที่ระดับหน้า เพื่อให้ค่าที่กรอกไว้ไม่หายเวลาสลับแท็บ)
 */
export const usePlayground = () => {
  const { apiKey, apiVersion, baseUrl } = useApiDocs();

  const [endpoint, setEndpoint] = useState('create-order');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<PlaygroundResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [apiSecret, setApiSecret] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [payoutNumber, setPayoutNumber] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({
    amount: '100',
    reference_id: 'TEST-' + Date.now(),
    bank_name: 'SCB',
    bank_number: '1234567890',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
  });
  const [payoutFields, setPayoutFields] = useState<Record<string, string>>({
    amount: '100',
    bank_account_name: 'ทดสอบ',
    bank_account_number: '1234567890',
    bank_name: 'SCB',
    reference_id: 'PAYOUT-TEST-' + Date.now(),
    note: '',
  });
  const [verifySlipFields, setVerifySlipFields] = useState<Record<string, string>>({
    order_number: '',
    qr_string: '',
    imageBase64: '',
    amount: '',
  });

  const selectEndpoint = useCallback((id: string) => {
    setEndpoint(id);
    setResponse(null);
    setError(null);
  }, []);

  const execute = useCallback(async () => {
    if (!apiSecret) {
      setError('กรุณากรอก API Secret ก่อน Send Request');
      return;
    }
    setLoading(true);
    setResponse(null);
    setError(null);
    const start = performance.now();
    const ep = PLAYGROUND_ENDPOINTS.find(e => e.id === endpoint);
    if (!ep) return;

    const apiBase = API_BASE_URL; // e.g. http://localhost:5000/api
    const vPrefix = `/${apiVersion}`;

    // Determine path & body
    let reqPath = '';
    let reqBody: any = null;
    let reqMethod = ep.method;

    if (endpoint === 'create-order') {
      reqPath = `${vPrefix}/orders`;
      reqBody = { amount: parseFloat(fields.amount) || 100, reference_id: fields.reference_id, bank_name: fields.bank_name, bank_number: fields.bank_number };
      if (fields.customer_name) reqBody.customer_name = fields.customer_name;
      if (fields.customer_email) reqBody.customer_email = fields.customer_email;
      if (fields.customer_phone) reqBody.customer_phone = fields.customer_phone;
    } else if (endpoint === 'get-order') {
      reqPath = `${vPrefix}/orders/${orderNumber}`;
    } else if (endpoint === 'list-orders') {
      reqPath = `${vPrefix}/orders`;
    } else if (endpoint === 'create-payout') {
      reqPath = `${vPrefix}/payout`;
      reqBody = {
        amount: parseFloat(payoutFields.amount) || 100,
        bank_account_name: payoutFields.bank_account_name,
        bank_account_number: payoutFields.bank_account_number,
        bank_name: payoutFields.bank_name,
      };
      if (payoutFields.reference_id) reqBody.reference_id = payoutFields.reference_id;
      if (payoutFields.note) reqBody.note = payoutFields.note;
    } else if (endpoint === 'get-payout') {
      reqPath = `${vPrefix}/payout/${payoutNumber}`;
    } else if (endpoint === 'get-balance') {
      reqPath = `${vPrefix}/wallet`;
    } else if (endpoint === 'get-banks') {
      reqPath = `${vPrefix}/banks`;
    } else if (endpoint === 'create-slip-order') {
      reqPath = `${vPrefix}/slip-orders`;
      reqBody = { amount: parseFloat(fields.amount) || 100, reference_id: fields.reference_id, bank_name: fields.bank_name, bank_number: fields.bank_number };
      if (fields.customer_name) reqBody.customer_name = fields.customer_name;
      if (fields.customer_email) reqBody.customer_email = fields.customer_email;
      if (fields.customer_phone) reqBody.customer_phone = fields.customer_phone;
    } else if (endpoint === 'get-banks-slips') {
      reqPath = `${vPrefix}/banks_slips`;
    } else if (endpoint === 'verify-slip') {
      reqPath = `${vPrefix}/orders/${verifySlipFields.order_number}/verify-slip`;
      reqBody = {};
      if (verifySlipFields.qr_string) reqBody.qr_string = verifySlipFields.qr_string;
      if (verifySlipFields.imageBase64) reqBody.imageBase64 = verifySlipFields.imageBase64;
    }

    // Build headers
    const headers: Record<string, string> = {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    };

    try {
      if (apiVersion === 'v2') {
        const timestamp = Math.floor(Date.now() / 1000).toString();
        const apiPath = `/api${reqPath}`;
        let payload = timestamp + reqMethod.toUpperCase() + apiPath;
        if (reqBody && Object.keys(reqBody).length > 0) {
          // SORT-KEY: ปัจจุบันเซ็นด้วย body ตามลำดับเดิม (ตรงกับ backend)
          // อนาคตถ้าใช้สคีมเรียง key → เปลี่ยนเป็น JSON.stringify(sortKeysDeep(reqBody))
          // และต้องส่ง reqBody ที่เรียงแล้วด้วย + แก้ backend ให้ sort ตรงกัน
          payload += JSON.stringify(reqBody);
        }
        const signature = await hmacSha256(apiSecret, payload);
        headers['X-Timestamp'] = timestamp;
        headers['X-Signature'] = signature;
      } else {
        headers['X-API-Secret'] = apiSecret;
      }

      const url = `${apiBase}${reqPath}`;
      const res = await axios({
        method: reqMethod.toLowerCase() as any,
        url,
        headers,
        data: reqBody || undefined,
        params: endpoint === 'list-orders' ? { limit: 5 } : undefined,
      });
      const time = Math.round(performance.now() - start);
      setResponse({ status: res.status, data: res.data, time });
    } catch (err: any) {
      const time = Math.round(performance.now() - start);
      if (err.response) {
        setResponse({ status: err.response.status, data: err.response.data, time });
      } else {
        setError(err.message || 'Network Error');
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, fields, payoutFields, orderNumber, payoutNumber, verifySlipFields, apiVersion, apiSecret, apiKey]);

  /** คำสั่ง cURL ที่ตรงกับค่าที่กรอกอยู่ตอนนี้ */
  const buildCurl = (): string => {
    const ep = PLAYGROUND_ENDPOINTS.find(e => e.id === endpoint);
    if (!ep) return '';
    const url = baseUrl;
    const secret = apiSecret || 'YOUR_SECRET';

    const authHeaders = apiVersion === 'v2'
      ? `  -H "X-API-Key: ${apiKey}" \\\n  -H "X-Timestamp: <timestamp>" \\\n  -H "X-Signature: <hmac_signature>"`
      : `  -H "X-API-Key: ${apiKey}" \\\n  -H "X-API-Secret: ${secret}"`;

    let curlCmd = '';

    if (endpoint === 'create-order') {
      const body: any = { amount: parseFloat(fields.amount) || 100, reference_id: fields.reference_id, bank_name: fields.bank_name, bank_number: fields.bank_number };
      if (fields.customer_name) body.customer_name = fields.customer_name;
      curlCmd = `curl -X POST "${url}/orders" \\\n${authHeaders} \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 2)}'`;
    } else if (endpoint === 'get-order') {
      curlCmd = `curl -X GET "${url}/orders/${orderNumber || ':order_number'}" \\\n${authHeaders}`;
    } else if (endpoint === 'list-orders') {
      curlCmd = `curl -X GET "${url}/orders?limit=5" \\\n${authHeaders}`;
    } else if (endpoint === 'create-payout') {
      const body: any = { amount: parseFloat(payoutFields.amount) || 100, bank_account_name: payoutFields.bank_account_name, bank_account_number: payoutFields.bank_account_number, bank_name: payoutFields.bank_name };
      curlCmd = `curl -X POST "${url}/payout" \\\n${authHeaders} \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 2)}'`;
    } else if (endpoint === 'get-payout') {
      curlCmd = `curl -X GET "${url}/payout/${payoutNumber || ':payout_number'}" \\\n${authHeaders}`;
    } else if (endpoint === 'get-balance') {
      curlCmd = `curl -X GET "${url}/wallet" \\\n${authHeaders}`;
    } else if (endpoint === 'get-banks') {
      curlCmd = `curl -X GET "${url}/banks" \\\n${authHeaders}`;
    } else if (endpoint === 'create-slip-order') {
      const body: any = { amount: parseFloat(fields.amount) || 100, reference_id: fields.reference_id, bank_name: fields.bank_name, bank_number: fields.bank_number };
      if (fields.customer_name) body.customer_name = fields.customer_name;
      curlCmd = `curl -X POST "${url}/slip-orders" \\\n${authHeaders} \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 2)}'`;
    } else if (endpoint === 'get-banks-slips') {
      curlCmd = `curl -X GET "${url}/banks_slips" \\\n${authHeaders}`;
    } else if (endpoint === 'verify-slip') {
      const body: Record<string, string> = {};
      if (verifySlipFields.qr_string) body.qr_string = verifySlipFields.qr_string;
      else if (verifySlipFields.imageBase64) body.imageBase64 = verifySlipFields.imageBase64.length > 60 ? verifySlipFields.imageBase64.substring(0, 60) + '...' : verifySlipFields.imageBase64;
      if (Object.keys(body).length > 0) {
        curlCmd = `curl -X POST "${url}/orders/${verifySlipFields.order_number || ':order_number'}/verify-slip" \\\n${authHeaders} \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 2)}'`;
      } else {
        curlCmd = `curl -X POST "${url}/orders/${verifySlipFields.order_number || ':order_number'}/verify-slip" \\\n${authHeaders} \\\n  -F "file=@/path/to/slip.jpg"`;
      }
    }
    return curlCmd;
  };

  return {
    endpoint,
    selectEndpoint,
    loading,
    response,
    error,
    apiSecret,
    setApiSecret,
    orderNumber,
    setOrderNumber,
    payoutNumber,
    setPayoutNumber,
    fields,
    setFields,
    payoutFields,
    setPayoutFields,
    verifySlipFields,
    setVerifySlipFields,
    execute,
    buildCurl,
  };
};

export type PlaygroundController = ReturnType<typeof usePlayground>;
